import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Search, Plus, RefreshCw, LineChart, IndianRupee, TrendingUp, Users,
  ChevronRight, Zap, Calendar, Target,
} from 'lucide-react';
import useApiMethods from '../../lib/useApiMethods';
import { cn } from '../../utils/tailwindUtils';


const PLATFORM_LABEL = {
  google_ads: 'Google Ads',
  meta_ads: 'Meta / Facebook Ads',
  instagram_ads: 'Instagram Ads',
  youtube_ads: 'YouTube Ads',
  linkedin_ads: 'LinkedIn Ads',
  justdial: 'JustDial',
  practo: 'Practo',
  sulekha: 'Sulekha',
  google_business: 'Google Business',
  email_campaign: 'Email',
  sms_campaign: 'SMS',
  whatsapp_campaign: 'WhatsApp',
  influencer: 'Influencer',
  other_directory: 'Other Directory',
  other: 'Other',
};

const PLATFORM_BADGE = {
  google_ads:     'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  meta_ads:       'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-300',
  instagram_ads:  'bg-pink-100 text-pink-700 dark:bg-pink-900/30 dark:text-pink-300',
  youtube_ads:    'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
  linkedin_ads:   'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
  justdial:       'bg-orange-100 text-orange-700 dark:bg-orange-900/30 dark:text-orange-300',
  practo:         'bg-teal-100 text-teal-700 dark:bg-teal-900/30 dark:text-teal-300',
  sulekha:        'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  google_business:'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  email_campaign: 'bg-purple-100 text-purple-700 dark:bg-purple-900/30 dark:text-purple-300',
  sms_campaign:   'bg-cyan-100 text-cyan-700 dark:bg-cyan-900/30 dark:text-cyan-300',
  whatsapp_campaign: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
};

const STATUS_BADGE = {
  planned: 'bg-slate-100 text-slate-700 dark:bg-slate-800 dark:text-slate-300',
  active:  'bg-green-100 text-green-700 dark:bg-green-900/30 dark:text-green-300',
  paused:  'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  ended:   'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-300',
};

function formatINR(n) {
  return Number(n || 0).toLocaleString('en-IN', { maximumFractionDigits: 0 });
}


export default function DigitalMarketingList() {
  const { fetch: apiFetch } = useApiMethods();
  const navigate = useNavigate();
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [reloadTick, setReloadTick] = useState(0);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const resp = await apiFetch('engage/digital-marketing-campaigns/');
        if (cancelled) return;
        setCampaigns(resp?.results || resp?.data?.results || []);
      } catch (e) {
        if (!cancelled) setError(e?.response?.data?.message || 'Failed to load campaigns');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reloadTick]);

  const filtered = useMemo(() => campaigns.filter(c => {
    if (statusFilter !== 'all' && c.status !== statusFilter) return false;
    if (search) {
      const q = search.trim().toLowerCase();
      const hay = [c.name, c.platform_display, c.target_department].filter(Boolean).join(' ').toLowerCase();
      if (!hay.includes(q)) return false;
    }
    return true;
  }), [campaigns, search, statusFilter]);

  const totals = useMemo(() => campaigns.reduce((acc, c) => ({
    spend: acc.spend + Number(c.spend || 0),
    budget: acc.budget + Number(c.budget || 0),
    leads: acc.leads + Number(c.linked_enquiries_count || 0),
    conversions: acc.conversions + Number(c.converted_count || 0),
  }), { spend: 0, budget: 0, leads: 0, conversions: 0 }), [campaigns]);

  return (
    <div className="p-4 max-w-[1600px] mx-auto space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center text-white shadow-sm">
            <LineChart className="w-5 h-5" />
          </div>
          <div>
            <h1 className="text-xl font-bold text-gray-900 dark:text-white">Digital Marketing</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              {loading ? 'Loading…' : `${filtered.length} of ${campaigns.length} campaigns`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setReloadTick(t => t + 1)} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700">
            <RefreshCw className="w-3.5 h-3.5" /> Refresh
          </button>
          <button onClick={() => navigate('/engage/digital-marketing/new')} className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-primary text-white text-sm shadow-sm hover:bg-primary/90">
            <Plus className="w-3.5 h-3.5" /> New Campaign
          </button>
        </div>
      </div>

      {/* KPI row */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2"><IndianRupee className="w-4 h-4 text-amber-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Total spend</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">₹ {formatINR(totals.spend)}</div>
          <div className="text-[10px] text-gray-500 mt-0.5">of ₹ {formatINR(totals.budget)} budget</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2"><Users className="w-4 h-4 text-blue-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Leads</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">{totals.leads}</div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2"><TrendingUp className="w-4 h-4 text-green-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Conversions</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">
            {totals.conversions}
            <span className="text-xs text-gray-500 ml-1">({totals.leads ? Math.round(totals.conversions / totals.leads * 100) : 0}%)</span>
          </div>
        </div>
        <div className="bg-white dark:bg-gray-800 rounded-xl p-3 border border-gray-100 dark:border-gray-700 shadow-sm">
          <div className="flex items-center gap-2"><Zap className="w-4 h-4 text-purple-500" />
            <span className="text-[10px] uppercase tracking-wide text-gray-500 dark:text-gray-400 font-medium">Avg CPL</span>
          </div>
          <div className="text-xl font-bold mt-1 text-gray-900 dark:text-white">
            ₹ {formatINR(totals.leads ? totals.spend / totals.leads : 0)}
          </div>
        </div>
      </div>

      {/* Search + filters */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[220px] max-w-md">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search campaigns…"
            className="w-full pl-9 pr-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-500 text-gray-900 dark:text-white placeholder-gray-400"
          />
        </div>
        <select value={statusFilter} onChange={e => setStatusFilter(e.target.value)}
          className="px-3 py-2 text-sm rounded-lg bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-gray-900 dark:text-white">
          <option value="all">All statuses</option>
          <option value="planned">Planned</option>
          <option value="active">Active</option>
          <option value="paused">Paused</option>
          <option value="ended">Ended</option>
        </select>
      </div>

      {error && <div className="p-3 rounded-lg bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-sm text-red-700 dark:text-red-300">{error}</div>}

      {/* Card grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
        {loading ? (
          <div className="col-span-full text-center py-8 text-gray-500 dark:text-gray-400">Loading…</div>
        ) : filtered.length === 0 ? (
          <div className="col-span-full text-center py-8 text-gray-500">
            {campaigns.length ? 'No campaigns match your filters.' : 'No marketing campaigns yet.'}
          </div>
        ) : filtered.map(c => {
          const cpl = c.linked_enquiries_count > 0 ? Number(c.spend) / c.linked_enquiries_count : 0;
          const convPct = c.linked_enquiries_count > 0 ? Math.round((c.converted_count / c.linked_enquiries_count) * 100) : 0;
          const spendPct = Number(c.budget) > 0 ? Math.round(Number(c.spend) / Number(c.budget) * 100) : 0;
          return (
            <button key={c.id} onClick={() => navigate(`/engage/digital-marketing/${c.id}`)}
              className="group text-left bg-white dark:bg-gray-800 rounded-xl p-4 border border-gray-100 dark:border-gray-700 shadow-sm hover:shadow-md hover:border-blue-300 dark:hover:border-blue-700 transition-all">
              <div className="flex items-start justify-between mb-2">
                <div className="flex-1 min-w-0">
                  <div className="font-semibold text-sm text-gray-900 dark:text-white truncate">{c.name}</div>
                  <div className="flex items-center gap-1.5 mt-1">
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', PLATFORM_BADGE[c.platform] || 'bg-gray-100 text-gray-700')}>
                      {PLATFORM_LABEL[c.platform] || c.platform}
                    </span>
                    <span className={cn('inline-block px-2 py-0.5 rounded-full text-[10px] font-medium', STATUS_BADGE[c.status] || 'bg-gray-100')}>
                      {c.status_display || c.status}
                    </span>
                  </div>
                </div>
              </div>

              {c.target_department && (
                <div className="text-[11px] text-gray-500 dark:text-gray-400 flex items-center gap-1 mb-2">
                  <Target className="w-3 h-3" /> {c.target_department}
                </div>
              )}

              {/* Spend progress */}
              <div className="mb-3">
                <div className="flex justify-between text-[11px] text-gray-500 dark:text-gray-400 mb-0.5">
                  <span>₹ {formatINR(c.spend)} / ₹ {formatINR(c.budget)}</span>
                  <span>{spendPct}%</span>
                </div>
                <div className="h-1.5 bg-gray-100 dark:bg-gray-700 rounded-full overflow-hidden">
                  <div className={cn(
                    'h-full transition-all',
                    spendPct > 90 ? 'bg-red-500' : spendPct > 60 ? 'bg-amber-500' : 'bg-blue-500'
                  )} style={{ width: `${Math.min(100, spendPct)}%` }} />
                </div>
              </div>

              {/* Metrics */}
              <div className="grid grid-cols-3 gap-2 text-center pt-3 border-t border-gray-100 dark:border-gray-700">
                <div>
                  <div className="text-sm font-bold text-blue-600 dark:text-blue-400">{c.linked_enquiries_count || 0}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500">Leads</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-green-600 dark:text-green-400">{convPct}%</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500">Conv.</div>
                </div>
                <div>
                  <div className="text-sm font-bold text-amber-600 dark:text-amber-400">₹ {formatINR(cpl)}</div>
                  <div className="text-[9px] uppercase tracking-wide text-gray-500">CPL</div>
                </div>
              </div>

              <div className="flex items-center justify-between mt-3 pt-2 border-t border-gray-100 dark:border-gray-700 text-[11px] text-gray-500 dark:text-gray-400">
                <span className="inline-flex items-center gap-1">
                  <Calendar className="w-3 h-3" /> {c.start_date}{c.end_date ? ` → ${c.end_date}` : ''}
                </span>
                <ChevronRight className="w-3.5 h-3.5 text-gray-400 group-hover:text-blue-500 transition-colors" />
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
